// Sesión de la plataforma nueva (Cognito vía Amplify, el mismo pool que la de siempre).
//   <Acceso>{({ user, signOut }) => …}</Acceso>      (misma firma que el Authenticator de App.js)
// - Con sesión: monta la plataforma directo. El Authenticator de Amplify (formularios + su CSS) NO va en el
//   paquete principal: sólo se descarga si hace falta iniciar sesión (PantallaAcceso, chunk aparte).
// - La sesión se sigue igual que el AuthenticatorProvider de Amplify: getCurrentUser() al arrancar y los avisos
//   de Hub ('signedIn' / 'signedOut'). Cerrar sesión = signOut() de aws-amplify/auth (como sidebarmenu.jsx:685).
// - Título de la pestaña por dominio (App.js:690); después lo ajusta la organización (OrganizationContext).
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { branding } from '../../config/branding';

const PantallaAcceso = lazy(() => import('./PantallaAcceso'));

function useSesion() {
  const [sesion, setSesion] = useState({ estado: 'configurando', user: null });
  useEffect(() => {
    let vivo = true;
    const leer = () => getCurrentUser()
      .then((user) => { if (vivo) setSesion({ estado: 'con', user }); })
      .catch(() => { if (vivo) setSesion({ estado: 'sin', user: null }); });
    leer();
    const quitar = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') leer();
      if (payload.event === 'signedOut') setSesion({ estado: 'sin', user: null });
    });
    return () => { vivo = false; quitar(); };
  }, []);
  return sesion;
}

/** El CSS de Amplify declara ~1,460 variables en `:root` que hereda CADA elemento: con él cargado, cambiar el tema
 *  recalcula estilos ~2.4× más lento (medido por Visuales: 1,706 → 715 ms). Sólo hace falta en la pantalla de
 *  acceso: después de entrar se apaga la hoja (y se vuelve a prender si se cierra sesión). */
function cssDeAmplify(activo) {
  Array.from(document.styleSheets).forEach((hoja) => {
    let reglas = null;
    try { reglas = hoja.cssRules; } catch { return; }
    if (reglas?.[0]?.selectorText?.includes('[data-amplify-theme]')) hoja.disabled = !activo;
  });
}

export default function Acceso({ children }) {
  const { estado, user } = useSesion();
  const salir = useCallback(() => signOut().catch(() => { /* el aviso de Hub llega igual al limpiar tokens */ }), []);

  useEffect(() => {
    if (branding.pageTitle) document.title = branding.pageTitle;
  }, []);

  useEffect(() => {
    if (estado !== 'configurando') cssDeAmplify(estado === 'sin');
  }, [estado]);

  if (estado === 'configurando') return null;
  if (estado === 'sin') {
    return (
      <Suspense fallback={null}>
        <PantallaAcceso />
      </Suspense>
    );
  }
  return children({ user, signOut: salir });
}
